import { Component, Input, Output, EventEmitter, signal, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { PassportDetailsComponent } from '../passport-details/passport-details.component';
import { NavigationService } from '../shared/navigation.service';
import { DateTimeHelperService } from '../shared/date-time-helper.service';
import { ScrollPositionService } from '../shared/scroll-position.service';

// Auto-scroll constants for drag operations
const AUTO_SCROLL_THRESHOLD_TOP = 150; // pixels from top where scrolling activates (increased for better sensitivity)
const AUTO_SCROLL_THRESHOLD_BOTTOM = 150; // pixels from bottom/navbar
const MAX_SCROLL_SPEED = 20; // pixels per frame (increased slightly)

@Component({
  selector: 'app-receiver-passport-cell',
  standalone: true,
  imports: [CommonModule, PassportDetailsComponent],
  templateUrl: './receiver-passport-cell.component.html',
  styleUrl: './receiver-passport-cell.component.css'
})
export class ReceiverPassportCellComponent implements OnInit, OnDestroy {
  @Input() recordId = '';
  @Input() loadNumber = '';
  @Input() growerName = '';
  @Input() variety = '';
  @Input() bulker = '';
  @Input() haulier = '';
  @Input() scheduledText = '';
  @Input() vendorNumber = '';
  @Input() origin = '';
  @Input() destination = '';
  @Input() apn = '';
  @Input() apnName = '';
  @Input() vendorName = '';
  @Input() status: 'pending' | 'outgoing' | 'completed' = 'pending';
  @Input() styleState: 'normal' | 'pcr-change' | 'cancelled' = 'normal';
  @Input() isDragging = false;
  @Input() index = 0;
  @Input() draggable = true; // Control whether drag-and-drop is enabled

  @Output() longPressStart = new EventEmitter<TouchEvent | MouseEvent>();
  @Output() longPressEnd = new EventEmitter<void>();
  @Output() dragStart = new EventEmitter<void>();
  @Output() dragEnd = new EventEmitter<void>();
  @Output() reorder = new EventEmitter<{fromIndex: number, toIndex: number}>();

  isExpanded = signal(false);
  isLongPress = false;
  longPressTimer: any = null;
  touchStartY = 0;
  currentY = 0;
  isDraggingLocal = false;
  dragOffsetY = 0;
  originalIndex = 0;

  // Auto-scroll state
  private autoScrollAnimationId: number | null = null;
  private autoScrollDirection: 'up' | 'down' | null = null;
  private autoScrollSpeed = 0;

  private readonly dateTimeHelper = inject(DateTimeHelperService);
  private readonly scrollPositionService = inject(ScrollPositionService);

  constructor(
    private readonly navigationService: NavigationService,
    private readonly store: PassportDetailsStore
  ) {}

  ngOnInit(): void {
    // Format scheduled delivery text using shared service (only if it's a UTC timestamp, not already formatted)
    if (this.scheduledText && !this.scheduledText.includes(',')) {
      // Check if it's a UTC timestamp (contains 'T' and possibly 'Z') rather than already formatted
      const formatted = this.dateTimeHelper.formatScheduledText(this.scheduledText);
      if (formatted) {
        this.scheduledText = formatted;
      }
    }
  }

  ngOnDestroy(): void {
    this.stopAutoScroll();
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
    }
  }

  // Auto-scroll methods for drag operations
  private startAutoScroll(direction: 'up' | 'down', speed: number): void {
    // If already scrolling in the same direction with same speed, don't restart
    if (this.autoScrollAnimationId && this.autoScrollDirection === direction && this.autoScrollSpeed === speed) {
      return;
    }

    // Stop any existing scroll
    this.stopAutoScroll();

    this.autoScrollDirection = direction;
    this.autoScrollSpeed = speed;
    this.performAutoScroll();
  }

  private stopAutoScroll(): void {
    if (this.autoScrollAnimationId) {
      cancelAnimationFrame(this.autoScrollAnimationId);
      this.autoScrollAnimationId = null;
    }
    this.autoScrollDirection = null;
    this.autoScrollSpeed = 0;
  }

  private performAutoScroll(): void {
    if (!this.autoScrollDirection || this.autoScrollSpeed === 0) {
      return;
    }

    const scrollAmount = this.autoScrollDirection === 'up' ? -this.autoScrollSpeed : this.autoScrollSpeed;
    window.scrollBy({ top: scrollAmount, behavior: 'auto' });

    // Continue scrolling
    this.autoScrollAnimationId = requestAnimationFrame(() => this.performAutoScroll());
  }

  private handleAutoScrollDuringDrag(clientY: number): void {
    const viewportHeight = window.innerHeight;
    
    // Check if near top edge
    if (clientY < AUTO_SCROLL_THRESHOLD_TOP) {
      const distanceFromEdge = clientY;
      const ratio = (AUTO_SCROLL_THRESHOLD_TOP - distanceFromEdge) / AUTO_SCROLL_THRESHOLD_TOP;
      const clampedRatio = Math.min(Math.max(ratio, 0), 1);
      const speed = Math.ceil(clampedRatio * MAX_SCROLL_SPEED);
      this.startAutoScroll('up', speed);
    }
    // Check if near bottom edge (accounting for navbar)
    else if (clientY > viewportHeight - AUTO_SCROLL_THRESHOLD_BOTTOM) {
      const distanceFromEdge = viewportHeight - clientY;
      const ratio = (AUTO_SCROLL_THRESHOLD_BOTTOM - distanceFromEdge) / AUTO_SCROLL_THRESHOLD_BOTTOM;
      const clampedRatio = Math.min(Math.max(ratio, 0), 1);
      const speed = Math.ceil(clampedRatio * MAX_SCROLL_SPEED);
      this.startAutoScroll('down', speed);
    }
    // Not near any edge, stop scrolling
    else {
      this.stopAutoScroll();
    }
  }

  onToggleExpand(): void {
    if (!this.isDraggingLocal) {
      this.isExpanded.set(!this.isExpanded());
    }
  }

  /**
   * Builds header data from component inputs for navigation state
   */
  private buildHeader(): any {
    return {
      loadNumber: this.loadNumber,
      variety: this.variety,
      haulier: this.haulier,
      vendorNumber: this.vendorNumber,
      vendorName: this.vendorName,
      growerName: this.growerName,
      origin: this.origin,
      destination: this.destination,
      apn: this.apn,
      apnName: this.apnName
    };
  }

  openDetails(): void {
    if (this.isDraggingLocal) return;
    
    // Ensure basic details exist in store for navigation
    const existing = this.store.get(this.recordId);
    if (!existing) {
      this.store.upsert({
        id: this.recordId,
        loadNumber: this.loadNumber,
        variety: this.variety,
        bulkerNumber: this.bulker,
        haulier: this.haulier,
        vendorNumber: this.vendorNumber,
        vendorName: this.vendorName,
        origin: this.origin,
        destination: this.destination,
        apn: this.apn,
        apnName: this.apnName
      });
    }

    this.scrollPositionService.saveScrollPosition('/receiver');
    this.scrollPositionService.setReturningFromDetail(true);
    this.navigationService.openPassportDetails(this.recordId, { from: 'receiver' });
  }

  openComments(): void {
    if (this.isDraggingLocal) return;

    this.scrollPositionService.saveScrollPosition('/receiver');
    this.scrollPositionService.setReturningFromDetail(true);
    const header = this.buildHeader();
    this.navigationService.openComments(this.recordId, header);
  }

  openHistory(): void {
    if (this.isDraggingLocal) return;

    this.scrollPositionService.saveScrollPosition('/receiver');
    this.scrollPositionService.setReturningFromDetail(true);
    const header = this.buildHeader();
    this.navigationService.openHistory(this.recordId, header);
  }

  // Touch event handlers for drag and drop
  onTouchStart(event: TouchEvent): void {
    // Don't initiate drag if draggable is false
    if (!this.draggable) return;
    
    this.touchStartY = event.touches[0].clientY;
    this.currentY = this.touchStartY;
    this.dragOffsetY = 0;
    this.originalIndex = this.index;
    
    this.longPressTimer = setTimeout(() => {
      this.isLongPress = true;
      this.isDraggingLocal = true;
      this.longPressStart.emit(event);
      this.dragStart.emit();
      
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
  }

  onTouchMove(event: TouchEvent): void {
    if (this.isLongPress) {
      event.preventDefault();
      this.currentY = event.touches[0].clientY;
      this.dragOffsetY = this.currentY - this.touchStartY;
      
      // Handle auto-scroll when near screen edges
      this.handleAutoScrollDuringDrag(this.currentY);
      
      // Cell stays in place with partial tilt - no manual transform needed
      // The .cell-dragging class handles the visual effect
    }
  }

  onTouchEnd(): void {
    this.handleDragEnd();
  }

  private handleDragEnd(): void {
    // Stop any active auto-scroll
    this.stopAutoScroll();
    
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
    }
    
    if (this.isLongPress) {
      // Calculate final position based on drag distance
      const cellHeight = 132; // Cell height + margin (120px + 12px)
      const steps = Math.round(this.dragOffsetY / cellHeight);
      const newIndex = Math.max(0, this.originalIndex + steps);
      
      // Emit reorder event if position changed
      if (newIndex !== this.originalIndex) {
        this.reorder.emit({ fromIndex: this.originalIndex, toIndex: newIndex });
      }
      
      this.longPressEnd.emit();
      this.dragEnd.emit();
      
      setTimeout(() => {
        this.isLongPress = false;
        this.isDraggingLocal = false;
        this.dragOffsetY = 0;
      }, 100);
    } else {
      this.isLongPress = false;
      this.isDraggingLocal = false;
    }
  }

  onTouchCancel(): void {
    // Stop any active auto-scroll
    this.stopAutoScroll();
    
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
    }
    
    this.longPressEnd.emit();
    this.isLongPress = false;
    this.isDraggingLocal = false;
    this.dragOffsetY = 0;
  }

  // Mouse event handlers for desktop drag and drop
  onMouseDown(event: MouseEvent): void {
    // Don't initiate drag if draggable is false
    if (!this.draggable) return;
    
    this.touchStartY = event.clientY;
    this.currentY = this.touchStartY;
    this.dragOffsetY = 0;
    this.originalIndex = this.index;
    
    this.longPressTimer = setTimeout(() => {
      this.isLongPress = true;
      this.isDraggingLocal = true;
      this.longPressStart.emit(event);
      this.dragStart.emit();
    }, 500);
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isLongPress) {
      event.preventDefault();
      this.currentY = event.clientY;
      this.dragOffsetY = this.currentY - this.touchStartY;
      
      // Handle auto-scroll when near screen edges
      this.handleAutoScrollDuringDrag(this.currentY);
      
      // Cell stays in place with partial tilt - no manual transform needed
      // The .cell-dragging class handles the visual effect
    }
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.handleDragEnd();
  }

  get formattedScheduledDelivery(): string {
    return this.scheduledText || 'Not scheduled';
  }

  get formattedApn(): string {
    return this.apn || '';
  }
}

